/**
 * LLM Input Coercion Utilities — "Sinks Not Pipes"
 *
 * LLMs frequently serialize complex values to JSON strings before passing them
 * as MCP tool arguments. These utilities wrap Zod schemas with z.preprocess
 * steps that coerce the common mis-serializations back to native types before
 * Zod validates them.
 *
 * Usage:
 *   import { jsonCoerce, boolCoerce, numCoerce } from '../coerce.js';
 *   // In a server.tool() schema:
 *   myArray: jsonCoerce(z.array(z.string())).describe('...'),
 *   myBool:  boolCoerce(z.boolean().optional()).describe('...'),
 *   myNum:   numCoerce(z.number().optional()).describe('...'),
 *
 * Design note: z.preprocess uses the INNER schema for JSON Schema generation,
 * so the MCP tool's inputSchema still shows the correct native type to the LLM.
 * The coercion is invisible to the model — it only fires when the model sends
 * the wrong type.
 */
import { z } from 'zod';

/**
 * JSON-coerce: If the input is a string, attempt JSON.parse before validation.
 * Handles LLM string-serialization of arrays (`"[...]"`), objects (`"{...}"`),
 * and records. If JSON.parse fails, the original string is passed through and
 * Zod produces a clear type error.
 */
export function jsonCoerce<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((v) => {
    if (typeof v === 'string') {
      try {
        return JSON.parse(v);
      } catch {
        return v; // Zod will reject it with a clear error
      }
    }
    return v;
  }, schema);
}

/**
 * Bool-coerce: Converts the strings "true" and "false" to their boolean
 * equivalents. Uses explicit string matching — NOT JavaScript truthiness —
 * so the string "false" correctly maps to boolean false.
 */
export function boolCoerce<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), schema);
}

/**
 * Num-coerce: Converts pure numeric strings ("42", "3.14") to numbers.
 * Non-numeric strings pass through unchanged (Zod produces a clear error).
 */
export function numCoerce<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((v) => {
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
    return v;
  }, schema);
}
